import { useEffect, useMemo, useState } from 'react';
import { Copy, Check, Heart, Loader2, Trash2, UserPlus2 } from 'lucide-react';
import { nip19 } from 'nostr-tools';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface SharePartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  myPubkey: string;
  currentPartner?: string;
  onSetPartner: (pubkey: string | null) => Promise<void>;
}

/** Decode any of npub1.../nprofile1.../hex into a hex pubkey. */
function decodePartner(input: string): string | null {
  const trimmed = input.trim().replace(/^nostr:/, '');
  if (!trimmed) return null;
  if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase();
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') return decoded.data;
    if (decoded.type === 'nprofile') return decoded.data.pubkey;
  } catch {
    return null;
  }
  return null;
}

export function SharePartnerDialog({
  open,
  onOpenChange,
  myPubkey,
  currentPartner,
  onSetPartner,
}: SharePartnerDialogProps) {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const myNpub = useMemo(() => nip19.npubEncode(myPubkey), [myPubkey]);
  const partnerAuthor = useAuthor(currentPartner);
  const partnerMeta = partnerAuthor.data?.metadata;

  // Reset the input each time the dialog reopens.
  useEffect(() => {
    if (open) setInput('');
  }, [open]);

  const decoded = input ? decodePartner(input) : null;
  const isInvalid = input.trim().length > 0 && !decoded;
  const isSelf = decoded && decoded === myPubkey;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(myNpub);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: "Couldn't copy",
        description: 'Please copy your npub manually.',
        variant: 'destructive',
      });
    }
  };

  const handleConnect = async () => {
    if (!decoded || isSelf) return;
    setSubmitting(true);
    try {
      await onSetPartner(decoded);
      toast({
        title: 'Connected',
        description: 'Your list will sync once they share back with you.',
      });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Couldn't connect",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnpair = async () => {
    setSubmitting(true);
    try {
      await onSetPartner(null);
      toast({ title: 'Unpaired' });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Couldn't unpair",
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const partnerName = currentPartner
    ? partnerMeta?.name ?? genUserName(currentPartner)
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="size-5 text-coral fill-current" />
            Invite your sweetheart
          </DialogTitle>
          <DialogDescription>
            Trade little codes — once you've both done it, your pantries will
            join hands.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Step 1 — share your npub */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                1
              </span>
              Send them your code
            </div>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={myNpub}
                onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-xs"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                aria-label="Copy your npub"
              >
                {copied ? <Check className="size-4 text-teal" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </section>

          {/* Step 2 — paste theirs */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                2
              </span>
              Paste their code
            </div>
            <Label htmlFor="partner-input" className="sr-only">
              Partner npub
            </Label>
            <Input
              id="partner-input"
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="npub1…"
              className="font-mono text-xs"
              aria-invalid={isInvalid}
            />
            {isInvalid && (
              <p className="text-xs text-destructive">
                That doesn't look like a valid npub or hex pubkey.
              </p>
            )}
            {isSelf && (
              <p className="text-xs text-destructive">
                That's your own code — you need theirs.
              </p>
            )}
          </section>

          {/* Currently paired */}
          {currentPartner && (
            <section className="rounded-xl border border-border/60 bg-muted/40 p-3">
              <div className="flex items-center gap-3">
                <Avatar className="size-9">
                  <AvatarImage src={sanitizeUrl(partnerMeta?.picture)} alt={partnerName} />
                  <AvatarFallback className="bg-primary/15 text-primary text-xs">
                    {partnerName.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{partnerName}</p>
                  <p className="text-[11px] text-muted-foreground truncate font-mono">
                    {nip19.npubEncode(currentPartner)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleUnpair}
                  disabled={submitting}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Unpair"
                  title="Unpair"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </section>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={!decoded || !!isSelf || submitting}
            className="min-w-32"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <UserPlus2 className="size-4 mr-1.5" />
                {currentPartner ? 'Update' : 'Connect'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
